import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, Injector, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, interval } from 'rxjs';
import { KundenportalInfoApiService } from '@bpp-stella/api/stella/api/kundenportal-info-api.service';
import { KundenportalInfoDto } from '@bpp-stella/api/stella/model/models';
import { KUNDENPORTAL_BUNDLE_VERSION } from './app-version';
import { compareVersions } from './version.util';

/** localStorage key holding the served version this browser last reset for. */
export const APPLIED_VERSION_KEY = 'kundenportal-applied-version';

/** Re-check cadence for a tab that stays open and idle in the foreground. */
export const CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Forced reset on a Kundenportal deploy — the cockpit's `BppFrontendMigrationService`, ported,
 * with one difference: the user is told first.
 *
 * `GET /api/kundenportal-info` (anonymous) serves the `kundenportal` row of `ui_configs`. When
 * its version is higher than the bundle running here (`KUNDENPORTAL_BUNDLE_VERSION`, package.json)
 * and higher than the one this browser last reset for, the update becomes `pendingUpdate` and
 * `KundenportalUpdateDialogComponent` (hosted by the app root) blocks the page until the user
 * confirms. `applyPendingUpdateAsync` then logs out and clears the storage as the row's flags say,
 * remembers the served version and reloads — which is what fetches the new bundle.
 *
 * Two comparisons on purpose: the bundle check keeps a browser that already runs the served
 * version quiet (a first visit, a fresh profile), the marker is the loop guard the cockpit
 * learnt the hard way — when the reload still lands on the old bundle (cache, dev image without a
 * package.json bump), a bumped row resets every user exactly once and the reload cannot loop.
 *
 * Checked at boot, every time the tab comes back to the foreground and every hour, so a tab
 * left open across a deploy picks it up the next time the user returns — or on its own if the
 * user never leaves it.
 */
@Injectable({ providedIn: 'root' })
export class KundenportalVersionService {
  private readonly kundenportalInfoApi = inject(KundenportalInfoApiService);
  private readonly bundleVersion = inject(KUNDENPORTAL_BUNDLE_VERSION);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  /** The served info that outranks this browser's marker; null while the portal is current. */
  private readonly _pendingUpdate: WritableSignal<KundenportalInfoDto | null> = signal(null);
  public readonly pendingUpdate: Signal<KundenportalInfoDto | null> = computed(() => this._pendingUpdate());

  private started = false;

  /** Runs the first check now and re-checks on every visibility change and every hour. Idempotent. */
  public start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.check();
    this.document.addEventListener('visibilitychange', () => {
      if (this.document.visibilityState === 'visible') {
        this.check();
      }
    });
    interval(CHECK_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.check());
  }

  /**
   * The reset the user confirmed in the dialog: log out and clear storage as the row says (in that
   * order), store the marker and reload. No-op without a pending update.
   */
  public async applyPendingUpdateAsync(): Promise<void> {
    const update = this._pendingUpdate();
    if (!update?.versionNumber) {
      return;
    }

    if (update.forceLogout) {
      // Before any clear: the logout deactivates browser push only while `app-push-person` is
      // still in localStorage. Lazy on purpose — LogoutService pulls every feature state service,
      // none of which belongs in the initial chunk for a check that mostly does nothing.
      try {
        const { LogoutService } = await import('../auth/logout.service');
        await this.injector.get(LogoutService).logoutAsync();
      } catch (error) {
        // The reload below still lands on a fresh bundle; a stuck logout must not block it.
        console.error('Logout before the Kundenportal update failed:', error);
      }
    }

    if (update.clearLocalStorage) {
      localStorage.clear();
      sessionStorage.clear();
    }
    // After any clear and before the reload, so the reloaded page sees itself as current.
    localStorage.setItem(APPLIED_VERSION_KEY, update.versionNumber);

    this.document.defaultView?.location.reload();
  }

  private check(): void {
    // The blocking dialog is already up and keeps the first update; another request changes nothing.
    if (this._pendingUpdate()) {
      return;
    }

    this.kundenportalInfoApi
      .apiKundenportalInfoGet()
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // A failed check must never get in the way of the portal — try again on the next trigger.
          console.error('An error occurred in KundenportalVersionService:', error);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((info) => this.detectUpdate(info));
  }

  private detectUpdate(info: KundenportalInfoDto): void {
    const servedVersion = info.versionNumber;
    if (!servedVersion || this._pendingUpdate()) {
      return;
    }

    // This bundle already is the served version (or newer): nothing to reset for.
    if (compareVersions(servedVersion, this.bundleVersion) <= 0) {
      return;
    }

    // Already reset for this served version and still on the old bundle: do not loop.
    const appliedVersion = this.readAppliedVersion();
    if (appliedVersion !== null && compareVersions(servedVersion, appliedVersion) <= 0) {
      return;
    }

    this._pendingUpdate.set(info);
  }

  private readAppliedVersion(): string | null {
    try {
      return localStorage.getItem(APPLIED_VERSION_KEY);
    } catch {
      return null;
    }
  }
}
